import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { ProcessedAutocompleteItem } from '../core/types';

export interface AutocompletePopupProps {
  /** Whether the popup is visible */
  isOpen: boolean;
  /** Items to display */
  items: ProcessedAutocompleteItem[];
  /** Position for the popup */
  position: { x: number; y: number } | null;
  /** Currently selected index */
  selectedIndex: number;
  /** Called when an item is clicked */
  onSelect: (index: number) => void;
  /** Called when popup should close */
  onClose: () => void;
}

export const AutocompletePopup: React.FC<AutocompletePopupProps> = ({
  isOpen,
  items,
  position,
  selectedIndex,
  onSelect,
  onClose,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (!isOpen || !popupRef.current) return;

    const selectedElement = popupRef.current.querySelector(
      `[data-index="${selectedIndex}"]`
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen, selectedIndex]);

  const handleItemClick = useCallback(
    (index: number, isError: boolean) => {
      if (!isError) {
        onSelect(index);
      }
    },
    [onSelect]
  );

  if (!isOpen || !position) return null;

  // Adjust position to avoid going off-screen
  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 320),
    y: position.y,
  };

  // Check if popup would go below viewport
  const popupHeight = Math.min(items.length * 48, 240);
  const wouldOverflow = position.y + popupHeight > window.innerHeight;
  if (wouldOverflow) {
    // Position above the cursor instead
    adjustedPosition.y = position.y - popupHeight - 24;
  }

  return createPortal(
    <div
      ref={popupRef}
      className="autocomplete-popup"
      style={{
        position: 'fixed',
        left: `${adjustedPosition.x}px`,
        top: `${adjustedPosition.y}px`,
        zIndex: 9999,
      }}
    >
      <div className="autocomplete-list">
        {items.length === 0 ? (
          <div className="autocomplete-empty">No suggestions</div>
        ) : (
          items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              data-index={index}
              className={`autocomplete-item ${
                index === selectedIndex ? 'selected' : ''
              } ${item.isError ? 'error' : ''}`}
              onClick={() => handleItemClick(index, !!item.isError)}
              disabled={item.isError}
            >
              <div className="autocomplete-item-content">
                <span className="autocomplete-item-label">{item.label}</span>
                {item.expandsTo && (
                  <span className="autocomplete-item-expansion">
                    → <code>{item.expandsTo}</code>
                  </span>
                )}
              </div>
              {item.description && (
                <span
                  className={
                    item.isError
                      ? 'autocomplete-error-message'
                      : 'autocomplete-item-description'
                  }
                >
                  {item.description}
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>,
    document.body
  );
};

export default AutocompletePopup;
